import * as React from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import * as Sentry from '@sentry/nextjs';
import { useRouter } from 'next/router';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { COACH_HOME_WITH_QUICK_ONBOARD_PAGE, ROOT_PAGE } from 'constants/pages';
import { AccountType, getIsValidUsernameFormat } from 'constants/user';
import {
  CoachStatusEnum,
  useCheckUsernameAvailabilityLazyQuery,
  useGetClosestCitiesLazyQuery,
  useGetGroupByAccessCodeLazyQuery,
  useInsertUserGroupMembershipMutation,
  useUpdateUserBasicFieldsMutation,
} from 'types/generated/client';
import { facebookTrackCompleteRegistration } from 'services/client/analytics/facebook';
import { OAUTH_SIGNIN_STATE_KEY } from 'services/client/oauthStatePayload';
import { milesToMeters } from 'utils/shared/geo/milesToMeters';
import { useGeoLocation } from 'hooks/useGeoLocation';
import { useGetCurrentUser } from 'hooks/useGetCurrentUser';
import Lock from 'svg/Lock';
import { Button } from 'components/Button';
import AddressSearchEnhanced from 'components/forms/AddressSearch/AddressSearchEnhanced';
import { findBestCityMatch } from 'components/forms/FormSignup/utils';
import Modal from 'components/modals/Modal';
import classNames from 'styles/utils/classNames';

const CITY_DISTANCE_MILES = 100;

const completeProfileSchema = z.object({
  fullName: z
    .string()
    .trim()
    .min(2, 'Full name must be at least 2 characters')
    .max(100, 'Full name must be 100 characters or less'),
  username: z
    .string()
    .max(36)
    .refine(
      (val) => !val || getIsValidUsernameFormat(val),
      'Username must contain only letters, numbers, and underscores',
    )
    .optional(),
  accountType: z.nativeEnum(AccountType),
});

type FormData = z.infer<typeof completeProfileSchema>;

interface StoredOAuthState {
  username?: string;
  accountType?: AccountType;
  initialLat?: number;
  initialLng?: number;
}

const isAccountType = (value: unknown): value is AccountType =>
  typeof value === 'string' && Object.values(AccountType).includes(value as AccountType);

const isCoordinate = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

/**
 * OAuth (Google/Facebook) sign-ups land in the DB flagged `isPendingAccountDetails`
 * because they never filled out the signup form. This blocking modal collects the
 * missing fields (name, location, account type, optional username) and clears the flag.
 * It is mounted globally in `_app.tsx` and renders only for pending users.
 */
export default function ModalCompleteProfile() {
  const router = useRouter();
  const { user } = useGetCurrentUser();

  const {
    position,
    addressString,
    activeAutocompleteAddress,
    suggestedAddresses,
    handleSubmitAutocomplete,
    handleAutcompleteSuggestions,
    isLoadingAutocomplete,
    autocompleteInputLength,
    autocompleteMinLength,
    initializeGeoLocation,
  } = useGeoLocation();

  const [updateUserBasicFields] = useUpdateUserBasicFieldsMutation();
  const [insertUserGroupMembership] = useInsertUserGroupMembershipMutation();
  const [getGroupByAccessCode, { data: groupData }] = useGetGroupByAccessCodeLazyQuery();
  const [getClosestCities] = useGetClosestCitiesLazyQuery();
  const [checkUsernameAvailability] = useCheckUsernameAvailabilityLazyQuery();
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
  const [cityId, setCityId] = React.useState<string | null>(user?.activeCity?.id ?? null);
  const hasFetchedGroupRef = React.useRef(false);
  const hasInitializedRef = React.useRef(false);

  // Correct (non-inverted) gate: show ONLY for a real user awaiting profile completion.
  const shouldShowModal = Boolean(user?.id && user.isPendingAccountDetails === true);

  const accountTypeDefault =
    user?.coachStatus === CoachStatusEnum.Active ? AccountType.Coach : AccountType.Player;

  // An OAuth user's auto-assigned username is a uuid (fails the format check); don't seed it.
  const usernameDefault =
    user?.username && getIsValidUsernameFormat(user.username) ? user.username : '';

  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<FormData>({
    resolver: zodResolver(completeProfileSchema),
    defaultValues: {
      fullName: user?.fullName ?? '',
      username: usernameDefault,
      accountType: accountTypeDefault,
    },
    mode: 'onChange',
  });

  // One-shot init once a pending user loads: seed the name from their record, then apply the
  // values captured on the signup form before the popup (username / account type / position).
  // Runs once (ref-guarded) so it never clobbers what the user subsequently edits.
  React.useEffect(() => {
    if (!shouldShowModal || !user || hasInitializedRef.current || typeof window === 'undefined') {
      return;
    }
    hasInitializedRef.current = true;

    // Google/Facebook usually supply a display name at insert time.
    if (user.fullName) {
      setValue('fullName', user.fullName);
    }

    const serializedState = window.localStorage.getItem(OAUTH_SIGNIN_STATE_KEY);
    if (!serializedState) {
      return;
    }
    window.localStorage.removeItem(OAUTH_SIGNIN_STATE_KEY);

    let isSubscribed = true;

    const hydrate = async () => {
      try {
        const parsed = JSON.parse(serializedState) as StoredOAuthState | null;
        if (!parsed) {
          return;
        }

        // Apply the signup-form username unless they already have a real (non-auto) one.
        const currentUsernameIsAuto = !getIsValidUsernameFormat(user.username ?? '');
        if (parsed.username && currentUsernameIsAuto) {
          setValue('username', parsed.username, { shouldDirty: true, shouldValidate: true });
        }

        if (isAccountType(parsed.accountType)) {
          setValue('accountType', parsed.accountType, { shouldDirty: true, shouldValidate: true });
        }

        const lat = isCoordinate(parsed.initialLat) ? parsed.initialLat : null;
        const lng = isCoordinate(parsed.initialLng) ? parsed.initialLng : null;

        if (lat !== null && lng !== null && isSubscribed) {
          await initializeGeoLocation({
            skipLocationEstimation: true,
            initialConfig: { position: { latitude: lat, longitude: lng }, isExactCenter: true },
          });
        }
      } catch (error) {
        Sentry.captureException(error, {
          extra: { context: 'ModalCompleteProfile:hydrate' },
        });
      }
    };

    hydrate();

    return () => {
      isSubscribed = false;
    };
  }, [shouldShowModal, user, setValue, initializeGeoLocation]);

  // If the user arrived via a group invite (?code=), look the group up so we can join them.
  React.useEffect(() => {
    if (!router.isReady || !shouldShowModal || hasFetchedGroupRef.current) {
      return;
    }

    const codeQuery = router.query.code;
    if (typeof codeQuery === 'string' && codeQuery.trim().length > 0) {
      hasFetchedGroupRef.current = true;
      getGroupByAccessCode({ variables: { accessCode: codeQuery } }).catch((error) => {
        Sentry.captureException(error, {
          extra: { context: 'ModalCompleteProfile:getGroupByAccessCode', code: codeQuery },
        });
      });
    }
  }, [router.isReady, router.query.code, shouldShowModal, getGroupByAccessCode]);

  // Resolve the closest city whenever we have a position.
  React.useEffect(() => {
    if (!shouldShowModal || !position) {
      return;
    }

    getClosestCities({
      variables: {
        distance: milesToMeters(CITY_DISTANCE_MILES),
        from: { type: 'Point', coordinates: [position.longitude, position.latitude] },
      },
    })
      .then((response) => {
        const cities = response?.data?.cities ?? [];
        const bestMatch = findBestCityMatch(addressString || '', cities, position);
        if (bestMatch?.id) {
          setCityId(bestMatch.id);
        }
      })
      .catch((error) => {
        Sentry.captureException(error, {
          extra: { context: 'ModalCompleteProfile:getClosestCities' },
        });
      });
  }, [shouldShowModal, position, getClosestCities, addressString]);

  const groupId = groupData?.groups?.[0]?.id ?? null;

  const handleAddressSubmit = async (
    selection: google.maps.places.AutocompletePrediction | null,
  ) => {
    const selectedAddress = selection?.description || addressString || '';

    try {
      const newPosition = await handleSubmitAutocomplete(selection);
      if (!newPosition) {
        return;
      }

      const response = await getClosestCities({
        variables: {
          distance: milesToMeters(CITY_DISTANCE_MILES),
          from: { type: 'Point', coordinates: [newPosition.longitude, newPosition.latitude] },
        },
      });

      const cities = response?.data?.cities ?? [];
      const bestMatch = findBestCityMatch(selectedAddress, cities, newPosition);
      if (bestMatch?.id) {
        setCityId(bestMatch.id);
      }
    } catch (error) {
      Sentry.captureException(error, {
        extra: { context: 'ModalCompleteProfile:handleAddressSubmit', address: selectedAddress },
      });
    }
  };

  const onFormSubmit = async (data: FormData) => {
    if (!user?.id || !addressString) {
      return;
    }

    setIsSubmitting(true);
    setErrorMessage(null);

    const normalizedFullName = data.fullName.trim();
    const normalizedUsername = data.username?.trim().toLowerCase() ?? '';

    try {
      // Only validate availability for a freshly-chosen username (not the existing one).
      if (normalizedUsername && normalizedUsername !== user.username) {
        const availability = await checkUsernameAvailability({
          variables: { username: normalizedUsername },
        });
        const isTaken =
          (availability.data?.usernamesActive?.length ?? 0) > 0 ||
          (availability.data?.usernamesClaimed?.length ?? 0) > 0;

        if (isTaken) {
          setErrorMessage('That username is already taken. Please choose another.');
          setIsSubmitting(false);
          return;
        }
      }

      const isCoach = data.accountType === AccountType.Coach;

      await updateUserBasicFields({
        variables: {
          id: user.id,
          fullName: normalizedFullName,
          // Preserve the existing username if they didn't pick a new one.
          username: normalizedUsername || user.username || null,
          activeCityId: cityId ?? null,
          isPendingAccountDetails: false,
          coachStatus: isCoach ? CoachStatusEnum.Active : CoachStatusEnum.None,
        },
      });

      if (groupId) {
        await insertUserGroupMembership({ variables: { userId: user.id, groupId } });
      }

      facebookTrackCompleteRegistration({
        email: user.email,
        fullName: normalizedFullName,
        username: normalizedUsername || user.username,
        userId: user.id,
        isCoach,
        accountType: data.accountType,
      });

      // Clearing the pending flag unmounts this modal (shouldShowModal → false); route home.
      await router.push(isCoach ? COACH_HOME_WITH_QUICK_ONBOARD_PAGE : ROOT_PAGE);
    } catch (error) {
      Sentry.captureException(error, {
        extra: { context: 'ModalCompleteProfile:submit', userId: user.id },
      });
      setErrorMessage('We ran into an issue completing your signup. Please try again.');
      setIsSubmitting(false);
    }
  };

  if (!shouldShowModal || !user) {
    return null;
  }

  return (
    <Modal
      isOpen={shouldShowModal}
      handleClose={() => {}}
      classNameMaxWidth="sm:max-w-[40rem]"
      className="sm:rounded-[24px]"
    >
      <form onSubmit={handleSubmit(onFormSubmit)} className="flex flex-col">
        <div className="flex flex-col gap-1.5 px-ds-xl pt-ds-xl sm:px-ds-3xl sm:pt-ds-2xl">
          <h3 className="typography-product-heading-compact text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
            Complete your profile
          </h3>
          <p className="typography-product-body text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
            Review and update your information to get started
          </p>
        </div>

        <div className="flex flex-col gap-ds-lg px-ds-xl pb-ds-lg pt-ds-lg sm:gap-ds-xl sm:px-ds-3xl sm:pb-ds-3xl sm:pt-ds-xl">
          {errorMessage && (
            <div className="rounded-xl bg-color-bg-lightmode-error px-ds-lg py-ds-sm dark:bg-color-bg-darkmode-error">
              <p className="typography-product-caption text-color-error">{errorMessage}</p>
            </div>
          )}

          <div className="flex flex-col gap-ds-lg sm:gap-ds-xl">
            {/* Account type */}
            <div className="w-full">
              <label className="typography-product-element-label mb-2 block text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
                Account type
              </label>
              <Controller
                name="accountType"
                control={control}
                render={({ field }) => (
                  <div className="flex w-full gap-1 rounded-full bg-color-bg-lightmode-secondary p-1 dark:bg-color-bg-darkmode-secondary">
                    {[AccountType.Player, AccountType.Coach].map((option) => (
                      <button
                        key={option}
                        onClick={() => field.onChange(option)}
                        type="button"
                        disabled={isSubmitting}
                        className={classNames(
                          'typography-product-button-label-medium flex-1 rounded-full py-3 transition-colors',
                          field.value === option
                            ? 'bg-color-brand-primary text-color-text-darkmode-primary shadow-sm'
                            : 'text-color-text-lightmode-tertiary hover:text-color-text-lightmode-secondary dark:text-color-text-darkmode-tertiary dark:hover:text-color-text-darkmode-secondary',
                        )}
                      >
                        {option === AccountType.Player ? 'Player' : 'Coach'}
                      </button>
                    ))}
                  </div>
                )}
              />
            </div>

            {/* Email (read-only) */}
            <div className="w-full">
              <label className="typography-product-element-label mb-2 block text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
                Email
              </label>
              <div className="relative">
                <input
                  type="email"
                  value={user.email}
                  disabled
                  className="input-form cursor-not-allowed bg-color-bg-lightmode-secondary pr-12 text-color-text-lightmode-primary dark:bg-color-bg-darkmode-secondary dark:text-color-text-darkmode-secondary"
                />
                <Lock className="pointer-events-none absolute right-4 top-1/2 h-5 w-5 -translate-y-1/2 text-color-text-lightmode-tertiary dark:text-color-text-darkmode-tertiary" />
              </div>
            </div>

            {/* Full name */}
            <div className="w-full">
              <label className="typography-product-element-label mb-2 block text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
                Full name
              </label>
              <Controller
                name="fullName"
                control={control}
                render={({ field }) => (
                  <input
                    {...field}
                    type="text"
                    autoComplete="name"
                    placeholder="Enter your full name"
                    disabled={isSubmitting}
                    maxLength={100}
                    className={classNames(
                      'input-form bg-color-bg-lightmode-secondary text-color-text-lightmode-primary placeholder-color-text-lightmode-placeholder dark:bg-color-bg-darkmode-secondary dark:text-color-text-darkmode-primary dark:placeholder-color-text-darkmode-placeholder',
                      errors.fullName && 'border border-color-error focus:ring-color-error',
                    )}
                  />
                )}
              />
              {errors.fullName && (
                <p className="typography-product-caption mt-1.5 text-color-error">
                  {errors.fullName.message}
                </p>
              )}
            </div>

            {/* Username (optional) */}
            <div className="w-full">
              <label className="typography-product-element-label mb-2 block text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
                Username{' '}
                <span className="typography-product-caption text-color-text-lightmode-tertiary dark:text-color-text-darkmode-tertiary">
                  (optional)
                </span>
              </label>
              <Controller
                name="username"
                control={control}
                render={({ field }) => (
                  <input
                    {...field}
                    type="text"
                    placeholder="Choose a username"
                    disabled={isSubmitting}
                    minLength={4}
                    maxLength={36}
                    onChange={(e) => field.onChange(e.target.value.trim())}
                    className={classNames(
                      'input-form bg-color-bg-lightmode-secondary text-color-text-lightmode-primary placeholder-color-text-lightmode-placeholder dark:bg-color-bg-darkmode-secondary dark:text-color-text-darkmode-primary dark:placeholder-color-text-darkmode-placeholder',
                      errors.username && 'border border-color-error focus:ring-color-error',
                    )}
                  />
                )}
              />
              {errors.username ? (
                <p className="typography-product-caption mt-1.5 text-color-error">
                  {errors.username.message}
                </p>
              ) : (
                <p className="typography-product-caption mt-1.5 text-color-text-lightmode-tertiary dark:text-color-text-darkmode-tertiary">
                  Min 4 characters. Letters, numbers, and underscores only.
                </p>
              )}
            </div>

            {/* Location (managed by the geolocation context) */}
            <div className="w-full">
              <label className="typography-product-element-label mb-2 block text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
                Location <span className="text-color-error">*</span>
              </label>
              <AddressSearchEnhanced
                id="complete-profile-location"
                name="complete-profile-location"
                autoComplete="off"
                type="text"
                disabled={isSubmitting}
                handleSubmit={handleAddressSubmit}
                handleAutcompleteSuggestions={handleAutcompleteSuggestions}
                addressString={addressString}
                suggestedAddresses={suggestedAddresses.slice(0, 3)}
                activeAutocompleteAddress={activeAutocompleteAddress}
                placeholder="Your location"
                isLoadingAutocomplete={isLoadingAutocomplete}
                autocompleteInputLength={autocompleteInputLength}
                autocompleteMinLength={autocompleteMinLength}
                className={classNames(
                  'input-form bg-color-bg-lightmode-secondary text-color-text-lightmode-primary placeholder-color-text-lightmode-placeholder dark:bg-color-bg-darkmode-secondary dark:text-color-text-darkmode-primary dark:placeholder-color-text-darkmode-placeholder',
                  !addressString && 'border border-color-error focus:ring-color-error',
                )}
              />
              {!addressString && (
                <p className="typography-product-caption mt-1.5 text-color-error">
                  Please enter your location
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="px-ds-xl pb-ds-lg sm:px-ds-3xl sm:pb-ds-3xl">
          <Button
            variant="brand"
            size="lg"
            type="submit"
            className="shadow-lg"
            disabled={isSubmitting || !addressString}
            loading={isSubmitting}
          >
            {isSubmitting ? 'Saving...' : 'Continue'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
